<template>
  <!-- Edit details of a system in a side panel, instead of the old edit pop-up (US 9370) -->
  <V2SidePanel ref="panel"
               :kind="$t('system_hero.panel.kind')"
               :title="$t('system_hero.panel.edit_title')"
               :intro="$t('system_hero.panel.edit_intro')"
               :busy="saving"
               qid="system-edit-panel"
               @hidden="error = ''">
    <b-alert v-if="error" show variant="danger" qid="create-p-error">{{ error }}</b-alert>

    <!-- Logo: saved straight away, apart from the other fields -->
    <div class="v2-field" qid="system-edit-logo">
      <span class="v2-field__label">{{ $t('system_logo.label') }}</span>
      <div class="sx-logo">
        <span class="sx-logo__tile" :class="{ 'has-image': logo }">
          <img v-if="logo" :src="logo" alt="">
          <template v-else>{{ initials }}</template>
        </span>
        <div class="sx-logo__actions">
          <label class="v2-link sx-logo__upload" qid="system-logo-upload">
            <font-awesome-icon icon="cloud-upload-alt"/>{{ $t('system_logo.upload') }}
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" class="d-none" @change="upload">
          </label>
          <button type="button" class="v2-link sx-logo__ai" :disabled="finding" qid="system-logo-find" @click="findLogo">
            <font-awesome-icon :icon="finding ? 'spinner' : 'sparkles'" :class="{ 'fa-spin': finding }"/>{{ finding ? $t('system_logo.finding') : $t('system_logo.find') }}
          </button>
          <button v-if="logo" type="button" class="v2-link is-danger" qid="system-logo-remove" @click="removeLogo">
            <font-awesome-icon icon="times"/>{{ $t('system_logo.remove') }}
          </button>
        </div>
      </div>
      <div v-if="candidates" class="sx-logo__found">
        <small class="v2-field__note mt-0">{{ candidates.length ? $t('system_logo.pick', { vendor: vendor || system.name }) : $t('system_logo.none') }}</small>
        <div class="sx-logo__grid">
          <button v-for="(candidate, index) in candidates"
                  :key="index"
                  type="button"
                  class="sx-logo__candidate"
                  :title="candidate.source"
                  :qid="'system-logo-candidate-' + index"
                  @click="pickLogo(candidate)">
            <img :src="candidate.image" alt="">
            <small>{{ host(candidate.source) }}</small>
          </button>
        </div>
      </div>
      <small class="v2-field__note">{{ $t('system_logo.hint') }}</small>
    </div>

    <div class="v2-field">
      <label class="v2-field__label" for="sx-edit-name">{{ $t('system_create.name') }} <span class="v2-field__req">*</span></label>
      <b-form-input id="sx-edit-name" v-model="form.name" :maxLength="255" required qid="create-p-form-name"></b-form-input>
    </div>

    <div class="v2-field">
      <label class="v2-field__label" for="sx-edit-description">{{ $t('systems.create.description_label') }}</label>
      <b-form-textarea id="sx-edit-description" v-model="form.description" rows="3" max-rows="8" qid="system-edit-description"></b-form-textarea>
    </div>

    <div class="v2-field__two">
      <div class="v2-field">
        <label class="v2-field__label">{{ $t('systems_overview.missing.owner') }} <span class="v2-field__hint">{{ $t('system_hero.panel.owner_hint') }}</span></label>
        <multiselect v-model="owner"
                     :options="users"
                     track-by="id"
                     label="name"
                     :placeholder="$t('system_hero.panel.owner_placeholder')"
                     :select-label="$t('system.dropdown_select_label')"
                     :selected-label="$t('system.dropdown_selected_label')"
                     :deselect-label="$t('system.dropdown_deselect_label')"
                     qid="system-edit-owner"
                     @search-change="loadUsers">
          <template slot="option" slot-scope="props">
            <Avatar :object="props.option" size="xs" :noOwner="true"></Avatar><span class="ml-2">{{ props.option.name }}</span>
          </template>
        </multiselect>
      </div>
      <div class="v2-field">
        <label class="v2-field__label">{{ $t('system_hero.panel.contributors') }}</label>
        <multiselect v-model="contributors"
                     :options="contributorOptions"
                     :multiple="true"
                     track-by="id"
                     label="name"
                     :placeholder="$t('system_hero.panel.contributors_placeholder')"
                     :select-label="$t('system.dropdown_select_label')"
                     :selected-label="$t('system.dropdown_selected_label')"
                     :deselect-label="$t('system.dropdown_deselect_label')"
                     qid="system-edit-contributors"
                     @search-change="loadUsers"></multiselect>
      </div>
    </div>
    <small v-if="!owner && !contributors.length" class="v2-field__note">{{ $t('systems_overview.create_hint') }}</small>

    <div class="v2-field">
      <span class="v2-field__label">{{ $t('systems_overview.fields.runs_as') }}</span>
      <div class="sp-runs" role="radiogroup">
        <button v-for="value in runsAsValues"
                :key="value"
                type="button"
                role="radio"
                :aria-checked="form.runs_as === value ? 'true' : 'false'"
                :class="{ on: form.runs_as === value }"
                :qid="'system-edit-runs-as-' + value"
                @click="form.runs_as = form.runs_as === value ? null : value">{{ $t('systems_overview.runs_as.' + value) }}</button>
      </div>
      <small class="v2-field__note">{{ needsLocation ? $t('system_page.where.help_premises') : (getClient.contractBasicModule ? $t('system_page.where.help') : $t('system_page.where.help_basic')) }}</small>
    </div>

    <div v-if="needsLocation" class="v2-field">
      <label class="v2-field__label" for="sx-edit-location">{{ $t('systems_overview.fields.location') }}</label>
      <b-form-input id="sx-edit-location" v-model="form.location" :maxLength="255" :placeholder="$t('system_page.where.location_placeholder')" qid="system-edit-location"></b-form-input>
    </div>

    <div class="v2-field__two">
      <div class="v2-field">
        <span class="v2-field__label">{{ $t('system_page.data.structure') }}</span>
        <div class="sx-toggles">
          <button v-for="key in structureKeys"
                  :key="key"
                  type="button"
                  :aria-pressed="form[key] ? 'true' : 'false'"
                  :class="{ on: form[key] }"
                  :qid="'system-edit-' + key"
                  @click="form[key] = form[key] ? 0 : 1">
            <font-awesome-icon v-if="form[key]" icon="check"/>{{ $t('systems.table.' + key.replace('is_', '')) }}
          </button>
        </div>
      </div>
      <div class="v2-field">
        <label class="v2-field__label" for="sx-edit-since">{{ $t('system_hero.panel.in_use_since') }}</label>
        <b-form-input id="sx-edit-since"
                      v-model="form.in_use_since"
                      type="number"
                      min="1950"
                      :max="maxYear"
                      :placeholder="$t('system_hero.panel.in_use_since_placeholder')"
                      qid="system-edit-in-use-since"></b-form-input>
      </div>
    </div>

    <div class="v2-field">
      <label class="v2-field__label">{{ $t('systems.create.tags_label') }}</label>
      <multiselect v-model="tags"
                   :options="tagOptions"
                   :multiple="true"
                   :taggable="true"
                   track-by="name_translated"
                   label="name_translated"
                   :placeholder="$t('systems.create.tags_placeholder')"
                   :select-label="$t('system.dropdown_select_label')"
                   :selected-label="$t('system.dropdown_selected_label')"
                   :deselect-label="$t('system.dropdown_deselect_label')"
                   qid="create-do-tags-select"
                   @search-change="loadTags"
                   @tag="addTag"></multiselect>
    </div>

    <template #foot>
      <span v-if="system.updated_by" class="v2-panel__note">
        <font-awesome-icon icon="clock"/>{{ $t('system_hero.panel.last_change', { name: system.updated_by.name, when: fromNow(system.updated_at) }) }}
      </span>
      <span class="v2-panel__grow"></span>
      <b-button variant="secondary" qid="create-p-cancel-button" @click="$refs.panel.hide()">{{ $t('systems.create.cancel') }}</b-button>
      <b-button variant="success" :disabled="saving || !form.name" qid="create-p-submit-button" @click="save">
        <font-awesome-icon :icon="saving ? 'spinner' : 'check'" :class="{ 'fa-spin': saving }"/> {{ $t('system_hero.panel.save') }}
      </b-button>
    </template>
  </V2SidePanel>
</template>

<script>
  import moment from 'moment';
  import piincHttp from '@/util/configureAxios.js';
  import Avatar from '@/components/Avatar.vue';
  import V2SidePanel from '@/components/V2SidePanel.vue';
  import {visibleOnly, hostOf} from '@/util/logoImage.js';

  export default {
    name: 'SystemEditPanel',
    components: {
      Avatar,
      V2SidePanel
    },
    props: {
      system: { type: Object, required: true }
    },
    data: function() {
      return {
        form: {},
        owner: null,
        contributors: [],
        tags: [],
        users: [],
        tagOptions: [],
        error: '',
        saving: false,
        logo: null,
        finding: false,
        candidates: null,
        vendor: null,
        runsAsValues: ['premises', 'hosted', 'saas', 'physical'],
        structureKeys: ['is_structured', 'is_unstructured', 'is_physical'],
        maxYear: new Date().getFullYear() + 1
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      initials() {
        let words = (this.system.name || '').trim().split(/\s+/).filter(Boolean);
        return words.length > 1 ? (words[0][0] + words[1][0]).toUpperCase() : (words[0] || '').slice(0, 3);
      },
      needsLocation() {
        return ['premises', 'physical'].includes(this.form.runs_as);
      },
      contributorOptions() {
        return this.owner ? this.users.filter(user => user.id !== this.owner.id) : this.users;
      }
    },
    methods: {
      show() {
        let system = this.system;
        let users = system.users || [];
        this.form = {
          name: system.name,
          description: system.description || '',
          runs_as: system.runs_as || null,
          location: system.location || '',
          in_use_since: system.in_use_since || '',
          is_structured: system.is_structured ? 1 : 0,
          is_unstructured: system.is_unstructured ? 1 : 0,
          is_physical: system.is_physical ? 1 : 0
        };
        this.owner = users.find(user => user.id == system.owner) || null;
        this.contributors = users.filter(user => user.id != system.owner);
        this.tags = (system.tags_translated || []).map(tag => Object.assign({}, tag));
        this.logo = system.logo || null;
        this.candidates = null;
        this.loadUsers();
        this.loadTags();
        this.$refs.panel.show();
      },
      fromNow(value) {
        return moment(value).fromNow();
      },
      addTag(name) {
        let tag = { name: name, name_translated: name };
        this.tags.push(tag);
        this.tagOptions.push(tag);
      },
      save() {
        let self = this;
        if (!this.owner && this.contributors.length) {
          this.error = this.$t('system.owners_error_message');
          return;
        }
        let contributors = this.contributors.filter(user => !this.owner || user.id !== this.owner.id);
        let data = Object.assign({}, this.form, {
          location: this.needsLocation ? (this.form.location.trim() || null) : this.system.location,
          in_use_since: this.form.in_use_since ? Number(this.form.in_use_since) : null,
          tags: this.tags.map(tag => tag.name_translated),
          users: (this.owner ? [this.owner.id] : []).concat(contributors.map(user => user.id))
        });
        if (this.owner) {
          data.owner = this.owner.id;
        }

        this.saving = true;
        this.error = '';
        piincHttp.put('systems/' + this.system.slug, data).then(function(response) {
          self.saving = false;
          self.$refs.panel.hide();
          self.$toastr('success', self.$t('systems.update.success'));
          self.$emit('saved', response.data);
        }, function(error) {
          self.saving = false;
          self.error = (error.data && error.data.message) || self.$t('system_hero.panel.error');
        });
      },
      host: hostOf,
      logoSaved(logo, message) {
        this.logo = logo;
        this.candidates = null;
        this.$toastr('success', this.$t(message));
        this.$emit('logo', logo);
      },
      upload(event) {
        let self = this;
        let file = event.target.files[0];
        event.target.value = '';
        if (!file) {
          return;
        }
        let data = new FormData();
        data.append('file', file);
        piincHttp.post('systems/' + this.system.slug + '/logo', data, { headers: { 'Content-Type': 'multipart/form-data' } }).then(function(response) {
          self.logoSaved(response.data.logo, 'system_logo.saved');
        }, function(error) {
          self.error = (error.data && error.data.message) || self.$t('system_logo.invalid');
        });
      },
      // The AI names the vendor's website; its icons are offered to pick from, nothing is saved yet
      findLogo() {
        let self = this;
        this.finding = true;
        this.error = '';
        piincHttp.post('systems/' + this.system.slug + '/logo/find').then(function(response) {
          self.vendor = response.data.vendor;
          return visibleOnly(response.data.candidates).then(function(candidates) {
            self.finding = false;
            self.candidates = candidates;
          });
        }, function(error) {
          self.finding = false;
          self.error = (error.data && error.data.message) || self.$t('system_logo.ai_failed');
        });
      },
      pickLogo(candidate) {
        let self = this;
        piincHttp.post('systems/' + this.system.slug + '/logo', { image: candidate.image, source: candidate.source }).then(function(response) {
          self.logoSaved(response.data.logo, 'system_logo.saved');
        }, function(error) {
          self.error = (error.data && error.data.message) || self.$t('system_logo.invalid');
        });
      },
      removeLogo() {
        let self = this;
        piincHttp.delete('systems/' + this.system.slug + '/logo').then(function() {
          self.logoSaved(null, 'system_logo.removed');
        }, function() {});
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params: { client: this.getClient.id, keyword: query || undefined, per_page: 50 } }).then(function(response) {
          self.users = response.data.items;
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params: { client: this.getClient.slug, keyword: query || undefined } }).then(function(response) {
          self.tagOptions = response.data;
        }, function() {});
      }
    }
  }
</script>
